import React, { useState, useEffect } from 'react';
import axios from 'axios';
import brandPersonas from '../data/brandPersonas.json';
import VelvetLogo from '../icons/VL_WordmarkOnly_Light.svg';
import VectorIcon from '../icons/Vector.png';
import '../styles/GuestBrandVoiceDesigner.css';

// Helper function to generate adjacent persona (blend of two personas)
const generateAdjacentPersona = (persona1Id, persona2Id, label) => {
  const p1 = brandPersonas[persona1Id];
  const p2 = brandPersonas[persona2Id];

  if (!p1 || !p2) return null;

  return {
    id: label,
    name: `${p1.name} / ${p2.name}`,
    keywords: [...p1.keywords.slice(0, 2), p2.keywords[0]],
    explainer: `A balanced voice that combines a ${p1.keywords[0].toLowerCase()} approach with a ${p2.keywords[0].toLowerCase()} nature.`,
    traits: [
      ...p1.traits.slice(0, 2),
      ...p2.traits.slice(0, 2)
    ],
    guidelines: [
      ...p1.guidelines.slice(0, 4),
      ...p2.guidelines.slice(0, 3)
    ],
    introduction: `This hybrid voice merges two complementary communication styles, creating a unique blend that balances both approaches.`
  };
};

// Initialize all personas (main + adjacent) once
const initializeAllPersonas = () => {
  const allPersonas = { ...brandPersonas };

  // Generate all adjacent personas
  const adjacentPairs = [
    ['A', 'B', 'Ab'],
    ['B', 'C', 'Bc'],
    ['C', 'D', 'Cd'],
    ['D', 'E', 'De'],
    ['E', 'F', 'Ef'],
    ['F', 'G', 'Fg'],
    ['G', 'H', 'Gh'],
    ['H', 'I', 'Hi']
  ];

  adjacentPairs.forEach(([p1, p2, label]) => {
    const adjacent = generateAdjacentPersona(p1, p2, label);
    if (adjacent) {
      allPersonas[label] = adjacent;
    }
  });

  return allPersonas;
};

const GuestBrandVoiceDesigner = () => {
  const [screen, setScreen] = useState('initializing'); // initializing, url-input, url-selection, loading, tournament, results
  const [urlInput, setUrlInput] = useState('');
  const [userName, setUserName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [error, setError] = useState(null);
  const [suggestedUrls, setSuggestedUrls] = useState([]);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [selectedUrlTitle, setSelectedUrlTitle] = useState(null);
  const [isExtractingUrls, setIsExtractingUrls] = useState(false);

  // AI-generated questions state
  const [generatedQuestions, setGeneratedQuestions] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [questionMetadata, setQuestionMetadata] = useState(null);

  // Guest profile
  const [guestProfile, setGuestProfile] = useState(null);
  const [existingCompletion, setExistingCompletion] = useState(null);

  // Back button state
  const [previousRound, setPreviousRound] = useState(null);
  const [canGoBack, setCanGoBack] = useState(false);

  // Tournament state - initialize with all personas immediately
  const [tournamentState, setTournamentState] = useState({
    round: 1,
    winners: {},
    brandUrl: '',
    finalWinner: null,
    allPersonas: initializeAllPersonas()
  });

  // Timer state for progressive generation
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [canProceed, setCanProceed] = useState(false);
  const [isGeneratingNext, setIsGeneratingNext] = useState(false);
  const MIN_READ_TIME = 5; // 5 seconds minimum

  // Adjacency map for determining Q+ and Q- in rounds 9-10
  const adjacencyMap = {
    'A': { prev: null, next: 'Ab' },
    'Ab': { prev: 'A', next: 'B' },
    'B': { prev: 'Ab', next: 'Bc' },
    'Bc': { prev: 'B', next: 'C' },
    'C': { prev: 'Bc', next: 'Cd' },
    'Cd': { prev: 'C', next: 'D' },
    'D': { prev: 'Cd', next: 'De' },
    'De': { prev: 'D', next: 'E' },
    'E': { prev: 'De', next: 'Ef' },
    'Ef': { prev: 'E', next: 'F' },
    'F': { prev: 'Ef', next: 'Fg' },
    'Fg': { prev: 'F', next: 'G' },
    'G': { prev: 'Fg', next: 'Gh' },
    'Gh': { prev: 'G', next: 'H' },
    'H': { prev: 'Gh', next: 'Hi' },
    'Hi': { prev: 'H', next: 'I' },
    'I': { prev: 'Hi', next: null }
  };

  // Generate ONLY fixed pairings for rounds 1-4 (initial generation)
  const getInitialPairings = () => {
    const initialPairings = [];
    // Rounds 1-4: Fixed pairings (always needed)
    initialPairings.push([1, 'A', 'B']);
    initialPairings.push([2, 'C', 'D']);
    initialPairings.push([3, 'E', 'F']);
    initialPairings.push([4, 'G', 'H']);
    return initialPairings;
  };

  // Call 2: Generate blended voices for rounds 7-10
  const generateBlendedVoices = async () => {
    // Only generate once
    if (isGeneratingNext) return;

    try {
      setIsGeneratingNext(true);
      const blendedVoiceIds = ['Ab', 'Bc', 'Cd', 'De', 'Ef', 'Fg', 'Gh', 'Hi'];

      const response = await axios.post('/api/guest/brand-voice/generate-questions', {
        url: tournamentState.brandUrl,
        voice_ids: blendedVoiceIds,
        cached_content: questionMetadata
      });

      if (response.data.success) {
        // Merge blended posts into existing voice posts
        setVoicePosts((prev) => ({
          ...prev,
          ...response.data.posts
        }));
        console.log(`Guest: Generated ${Object.keys(response.data.posts).length} blended voice posts`);
      }
    } catch (error) {
      console.error('Guest: Error generating blended voices:', error);
    } finally {
      setIsGeneratingNext(false);
    }
  };

  // Pre-generate both possible paths for rounds 9 and 10
  const pregenerateFinalRound = async (round8Pairing, roundNumber) => {
    if (!round8Pairing) return;

    const { optionA, optionB } = round8Pairing;
    const pairingsToGenerate = [];

    if (roundNumber === 9) {
      // Generate both possible round 9s
      const optionA_next = adjacencyMap[optionA]?.next;
      const optionB_next = adjacencyMap[optionB]?.next;

      if (optionA_next) {
        pairingsToGenerate.push([9, optionA, optionA_next]);
      }
      if (optionB_next) {
        pairingsToGenerate.push([9, optionB, optionB_next]);
      }
    } else if (roundNumber === 10) {
      // Generate both possible round 10s
      const optionA_prev = adjacencyMap[optionA]?.prev;
      const optionB_prev = adjacencyMap[optionB]?.prev;

      if (optionA_prev) {
        pairingsToGenerate.push([10, optionA, optionA_prev]);
      }
      if (optionB_prev) {
        pairingsToGenerate.push([10, optionB, optionB_prev]);
      }
    }

    if (pairingsToGenerate.length === 0) return;

    try {
      console.log(`Pre-generating round ${roundNumber} for both paths:`, pairingsToGenerate);
      const response = await axios.post('/api/guest/brand-voice/generate-questions', {
        url: tournamentState.brandUrl,
        custom_pairings: pairingsToGenerate,
        cached_content: questionMetadata
      });

      if (response.data.success && response.data.questions.length > 0) {
        setGeneratedQuestions((prev) => {
          if (!prev) return response.data.questions;

          const newQuestions = response.data.questions.filter(newQ => {
            return !prev.some(existingQ =>
              existingQ.round === newQ.round &&
              existingQ.avatarA === newQ.avatarA &&
              existingQ.avatarB === newQ.avatarB
            );
          });

          return [...prev, ...newQuestions].sort((a, b) => a.round - b.round);
        });
        console.log(`Pre-generated ${response.data.questions.length} round ${roundNumber} questions`);
      }
    } catch (error) {
      console.error(`Error pre-generating round ${roundNumber}:`, error);
    }
  };

  // Dynamic tournament pairing logic - determines which avatars face off each round
  const getTournamentPairing = (round, winners) => {
    const pairings = {
      1: { optionA: 'A', optionB: 'B' },
      2: { optionA: 'C', optionB: 'D' },
      3: { optionA: 'E', optionB: 'F' },
      4: { optionA: 'G', optionB: 'H' },
      5: { optionA: 'I', optionB: winners[1] || 'A' }, // I vs J (winner of round 1)
      6: { optionA: winners[2] || 'C', optionB: winners[3] || 'E' }, // K vs L
      7: { optionA: winners[4] || 'G', optionB: winners[5] || 'I' }, // M vs N
      8: { optionA: winners[6] || 'C', optionB: winners[7] || 'G' }, // O vs P (championship)
    };

    // Rounds 9-10 use winners and adjacent personas
    if (round === 9) {
      const Q = winners[8]; // Round 8 winner
      // Try next first, fallback to prev for edge cases (e.g., I has no next, use Hi)
      const Qplus = Q ? (adjacencyMap[Q]?.next || adjacencyMap[Q]?.prev) : null;
      if (!Qplus) {
        // No adjacent voice available
        return null;
      }
      return { optionA: Q, optionB: Qplus };
    }

    if (round === 10) {
      const Q = winners[9]; // Round 9 winner

      // Calculate what Round 9's opponent was (to avoid duplicates)
      const round9Winner = winners[8];
      const round9Opponent = round9Winner ? (adjacencyMap[round9Winner]?.next || adjacencyMap[round9Winner]?.prev) : null;

      // Try prev first
      let Qminus = Q ? adjacencyMap[Q]?.prev : null;

      // If prev is null or would duplicate Round 9, find alternative
      if (!Qminus || (Qminus === round9Opponent && Q === round9Winner)) {
        // Edge case handling for voices A and I
        if (Q === 'A') {
          // Voice A: prev is null, next would be Ab (duplicate of R9)
          // Skip to B instead
          Qminus = 'B';
        } else if (Q === 'I') {
          // Voice I: prev is Hi (duplicate of R9)
          // Skip to H instead
          Qminus = 'H';
        } else if (Qminus === round9Opponent) {
          // Would duplicate Round 9, skip one more in the chain
          Qminus = Qminus ? adjacencyMap[Qminus]?.prev : null;
          if (!Qminus) {
            Qminus = Q ? adjacencyMap[Q]?.next : null;
          }
        } else {
          // Regular fallback to next
          Qminus = Q ? adjacencyMap[Q]?.next : null;
        }
      }

      if (!Qminus) {
        // No adjacent voice available
        return null;
      }

      return { optionA: Q, optionB: Qminus };
    }

    return pairings[round];
  };

  // Check for existing guest completion on mount
  useEffect(() => {
    checkGuestStatus();
  }, []);

  const checkGuestStatus = async () => {
    try {
      // First check if user is logged in as a regular user (not guest)
      try {
        const userProfileResponse = await axios.get('/api/user/profile');
        if (userProfileResponse.data && userProfileResponse.data.email) {
          // Regular user is logged in - show message to logout
          setGuestProfile({
            isRegularUser: true,
            email: userProfileResponse.data.email,
            name: userProfileResponse.data.name
          });
          setScreen('already-logged-in');
          return;
        }
      } catch (userError) {
        // 401, 403, or 404 means not logged in as regular user - continue with guest flow
        // These are expected responses, silently continue
      }

      // Check guest authentication
      const profileResponse = await axios.get('/api/guest/profile');
      setGuestProfile(profileResponse.data);

      // Check for existing completion
      const completionResponse = await axios.get('/api/guest/brand-voice/get-completion');
      if (completionResponse.data.completion) {
        setExistingCompletion(completionResponse.data.completion);

        // If completed, show results
        if (completionResponse.data.completion.status === 'completed') {
          setUserName(completionResponse.data.completion.guest_name || '');
          setBrandName(completionResponse.data.completion.brand_name || '');
          setTournamentState((prev) => ({
            ...prev,
            finalWinner: completionResponse.data.completion.final_winner,
            brandUrl: completionResponse.data.completion.brand_url,
            winners: completionResponse.data.completion.winners || {}
          }));
          setGeneratedQuestions(completionResponse.data.completion.generated_questions);
          setScreen('results');
          return;
        }
      }

      // No completion or incomplete - show URL input
      setScreen('url-input');
    } catch (error) {
      if (error.response?.status === 401) {
        // Not authenticated - show guest login screen (don't redirect away)
        setScreen('guest-login');
      } else {
        console.error('Error checking guest status:', error);
        setScreen('url-input');
      }
    }
  };

  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    if (!urlInput.trim() || !userName.trim() || !brandName.trim()) {
      setError('Please fill in all fields');
      return;
    }

    // Auto-add https:// if no protocol is present
    let originalUrl = urlInput.trim();
    if (!originalUrl.startsWith('http://') && !originalUrl.startsWith('https://')) {
      originalUrl = 'https://' + originalUrl;
    }

    let parsedUrl;
    try {
      parsedUrl = new URL(originalUrl);
    } catch (e) {
      setError('Please enter a valid URL');
      return;
    }

    setError(null);
    setIsExtractingUrls(true);

    try {
      // Always scrape the base domain (homepage) to get navigation links
      const baseDomain = `${parsedUrl.protocol}//${parsedUrl.hostname}`;

      const response = await axios.post('/api/guest/brand-voice/extract-urls', {
        url: baseDomain,
        max_urls: 5
      });

      if (response.data.success && response.data.urls && response.data.urls.length > 0) {
        // Always include the user's original URL as the first option
        const urls = [
          { url: originalUrl, title: 'Original URL' }
        ];

        // Add suggested URLs from homepage, but skip if it's the same as original or base domain
        response.data.urls.forEach(urlObj => {
          const normalizedUrl = urlObj.url.toLowerCase().replace(/\/$/, '');
          const normalizedOriginal = originalUrl.toLowerCase().replace(/\/$/, '');
          const normalizedBase = baseDomain.toLowerCase().replace(/\/$/, '');

          // Only add if it's different from the original URL and not already in the list
          if (normalizedUrl !== normalizedOriginal &&
              normalizedUrl !== normalizedBase &&
              !urls.some(u => u.url.toLowerCase().replace(/\/$/, '') === normalizedUrl)) {
            urls.push(urlObj);
          }
        });

        // If base domain is different from original, add it as "Homepage"
        if (baseDomain.toLowerCase() !== originalUrl.toLowerCase().replace(/\/$/, '')) {
          urls.push({ url: baseDomain, title: 'Homepage' });
        }

        setSuggestedUrls(urls);
        setScreen('url-selection');
      } else {
        // If extraction fails, proceed with original URL
        setSuggestedUrls([{ url: originalUrl, title: 'Original URL' }]);
        setScreen('url-selection');
      }
    } catch (error) {
      console.error('Error extracting URLs:', error);
      // If error, just proceed with original URL
      setSuggestedUrls([{ url: urlInput.trim(), title: 'Original URL' }]);
      setScreen('url-selection');
    } finally {
      setIsExtractingUrls(false);
    }
  };

  // NEW: State to store posts by voice ID
  const [voicePosts, setVoicePosts] = useState({});

  const handleUrlSelection = async (url, title) => {
    setSelectedUrl(url);
    setSelectedUrlTitle(title || 'Selected Page');
    setError(null);
    setScreen('loading');
    setTournamentState((prev) => ({ ...prev, brandUrl: url }));

    try {
      // Call 1: Generate posts for base voices (A-I) - covers rounds 1-6
      const baseVoiceIds = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];

      setIsGenerating(true);
      const response = await axios.post('/api/guest/brand-voice/generate-questions', {
        url: url,
        voice_ids: baseVoiceIds
      });

      if (response.data.success) {
        // Store posts by voice ID
        setVoicePosts(response.data.posts);
        setQuestionMetadata(response.data.scraped_content);
        setIsGenerating(false);

        // Start timer for round 1
        setTimeElapsed(0);
        setIsTimerActive(true);
        setCanProceed(false);

        setScreen('tournament');
      } else {
        throw new Error(response.data.error || 'Failed to generate questions');
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      setError('Failed to generate questions. Please try again.');
      setIsGenerating(false);
      setScreen('url-selection');
    }
  };

  const generateQuestionsBatch = async (url, startRound, endRound, cachedContent = null) => {
    try {
      setIsGenerating(true);

      const response = await axios.post('/api/guest/brand-voice/generate-questions', {
        url: url,
        start_round: startRound,
        end_round: endRound,
        cached_content: cachedContent
      });

      if (response.data.success) {
        const newQuestions = response.data.questions;

        setGeneratedQuestions((prev) => {
          if (!prev) return newQuestions;
          return [...prev, ...newQuestions].sort((a, b) => a.round - b.round);
        });

        if (!questionMetadata && response.data.scraped_content) {
          setQuestionMetadata(response.data.scraped_content);
        }
      } else {
        throw new Error(response.data.error || 'Failed to generate questions');
      }
    } catch (error) {
      console.error(`Error generating questions ${startRound}-${endRound}:`, error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate a single question for a specific round with exact avatar pairing
  const generateQuestionForRound = async (round, pairing, winners) => {
    try {
      // Custom pairing: [(round, avatarA, avatarB)]
      const customPairings = [[round, pairing.optionA, pairing.optionB]];

      const response = await axios.post('/api/guest/brand-voice/generate-questions', {
        url: tournamentState.brandUrl,
        start_round: round,
        end_round: round,
        cached_content: questionMetadata,
        custom_pairings: customPairings
      });

      if (response.data.success) {
        const newQuestion = response.data.questions[0];

        setGeneratedQuestions((prev) => {
          if (!prev) return [newQuestion];
          // Add new question and sort
          return [...prev, newQuestion].sort((a, b) => a.round - b.round);
        });
      } else {
        throw new Error(response.data.error || 'Failed to generate question');
      }
    } catch (error) {
      console.error(`Error generating question for round ${round}:`, error);
      throw error;
    }
  };

  const handleAnswerSelect = async (choice) => {
    // Prevent selection if timer hasn't completed
    if (!canProceed) return;

    const currentRound = tournamentState.round;

    // Get current pairing based on dynamic bracket logic
    const pairing = getTournamentPairing(currentRound, tournamentState.winners);

    if (!pairing) {
      // No valid pairing, tournament is complete
      const finalWinner = tournamentState.winners[currentRound - 1] || tournamentState.winners[8];
      const updatedState = { ...tournamentState, finalWinner };
      setTournamentState(updatedState);
      setScreen('results');
      await completeDesigner(finalWinner, tournamentState.winners);
      return;
    }

    // Determine winner based on choice and pairing
    const avatarChosen = choice === 'A' ? pairing.optionA : pairing.optionB;

    // Store previous state for back button
    setPreviousRound({
      round: currentRound,
      winners: { ...tournamentState.winners }
    });
    setCanGoBack(true);

    // Update winners
    const newWinners = { ...tournamentState.winners, [currentRound]: avatarChosen };
    const nextRound = currentRound + 1;

    // Check if we're done
    if (nextRound > 10) {
      const finalWinner = avatarChosen; // Winner of Round 10 is the final winner
      const updatedState = {
        ...tournamentState,
        winners: newWinners,
        finalWinner
      };
      setTournamentState(updatedState);
      setScreen('results');
      await completeDesigner(finalWinner, newWinners);
      return;
    }

    // Check if next pairing exists
    const nextPairing = getTournamentPairing(nextRound, newWinners);
    if (!nextPairing) {
      // No next pairing, we're done
      const finalWinner = avatarChosen;
      const updatedState = {
        ...tournamentState,
        winners: newWinners,
        finalWinner
      };
      setTournamentState(updatedState);
      setScreen('results');
      await completeDesigner(finalWinner, newWinners);
      return;
    }

    // Update UI and reset timer for next round
    setTournamentState({
      ...tournamentState,
      round: nextRound,
      winners: newWinners
    });

    // Reset timer for next round
    setTimeElapsed(0);
    setCanProceed(false);
    setIsTimerActive(true);

    // Call 2: Trigger after round 2 to generate blended voices for rounds 7-10
    if (nextRound === 3) {
      generateBlendedVoices();
    }

    // Save progress in background (non-blocking)
    axios.post('/api/guest/brand-voice/save-progress', {
      status: 'in_progress',
      current_round: currentRound,
      guest_name: userName,
      brand_name: brandName,
      brand_url: tournamentState.brandUrl,
      generated_questions: generatedQuestions,
      winners: newWinners
    }).catch((error) => {
      console.error('Error saving progress:', error);
    });
  };

  const handleGoBack = async () => {
    if (!canGoBack || !previousRound) return;

    setTournamentState({
      ...tournamentState,
      round: previousRound.round,
      winners: previousRound.winners
    });

    // Save progress with previous state
    try {
      await axios.post('/api/guest/brand-voice/save-progress', {
        status: 'in_progress',
        current_round: previousRound.round,
        guest_name: userName,
        brand_name: brandName,
        brand_url: tournamentState.brandUrl,
        generated_questions: generatedQuestions,
        winners: previousRound.winners
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }

    setPreviousRound(null);
    setCanGoBack(false);
  };

  const completeDesigner = async (finalWinner, winners) => {
    try {
      const personaData = tournamentState.allPersonas[finalWinner];

      await axios.post('/api/guest/brand-voice/complete', {
        guest_name: userName,
        brand_name: brandName,
        brand_url: tournamentState.brandUrl,
        generated_questions: generatedQuestions,
        winners: winners,
        final_winner: finalWinner,
        persona_data: personaData
      });

      setScreen('results');
    } catch (error) {
      console.error('Error completing designer:', error);
      setError('Failed to save results. Please try again.');
    }
  };

  const handleRetake = () => {
    // Reset state
    setScreen('url-input');
    setUrlInput('');
    setUserName('');
    setBrandName('');
    setError(null);
    setSuggestedUrls([]);
    setSelectedUrl(null);
    setGeneratedQuestions(null);
    setQuestionMetadata(null);
    setTournamentState({
      round: 1,
      winners: {},
      brandUrl: '',
      finalWinner: null,
      allPersonas: {}
    });
    setPreviousRound(null);
    setCanGoBack(false);
  };

  const getPersonaInfo = () => {
    if (!tournamentState.finalWinner) return null;
    return tournamentState.allPersonas[tournamentState.finalWinner];
  };

  // Map avatar ID to Brand Voice Type number (matches BrandVoiceDesigner)
  const getVoiceTypeNumber = (personaId) => {
    const mapping = {
      'A': '1', 'Ab': '1-2', 'B': '2', 'Bc': '2-3', 'C': '3', 'Cd': '3-4',
      'D': '4', 'De': '4-5', 'E': '5', 'Ef': '5-6', 'F': '6', 'Fg': '6-7',
      'G': '7', 'Gh': '7-8', 'H': '8', 'Hi': '8-9', 'I': '9'
    };
    return mapping[personaId] || '1';
  };

  // Render functions for each screen
  const renderGuestLogin = () => (
    <div className="guest-login-container">
      <div className="guest-login-card">
        <div className="guest-login-header">
          <img src={VelvetLogo} alt="Velvet" className="guest-login-logo" />
          <h1>Discover Your Brand Voice</h1>
          <p>Sign in with Google to get started</p>
        </div>

        <div className="guest-login-form">
          <a
            href="/auth/login?mode=guest"
            className="guest-google-login-btn"
          >
            <div className="google-icon">
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path
                  fill="#4285F4"
                  d="M9 8.182v2.727h4.636c-.201 1.636-1.636 2.727-4.636 2.727-2.727 0-4.909-2.182-4.909-4.909S6.273 4.364 9 4.364c1.227 0 2.318.409 3.182 1.182l2.045-2.045C12.682 2.045 10.955 1.364 9 1.364 4.636 1.364 1.091 4.909 1.091 9.227s3.545 7.864 7.909 7.864c4.5 0 7.636-3.182 7.636-7.636 0-.545-.055-1.091-.164-1.636H9z"
                />
              </svg>
            </div>
            <span>Continue with Google</span>
          </a>
        </div>

        <div className="guest-login-footer">
          <p>Velvet - Brand Voice Assessment</p>
        </div>
      </div>
    </div>
  );

  const renderAlreadyLoggedIn = () => (
    <div className="guest-login-container">
      <div className="guest-login-card">
        <div className="guest-login-header">
          <img src={VelvetLogo} alt="Velvet" className="guest-login-logo" />
          <h1>Already Signed In</h1>
          <p>You're currently signed in as <strong>{guestProfile?.email}</strong></p>
        </div>

        <div className="guest-login-form">
          <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
            To use the guest brand voice assessment, please log out of your account first.
          </p>
          <a
            href="/auth/logout"
            className="guest-google-login-btn"
            style={{ backgroundColor: 'var(--color-danger-button)' }}
          >
            <span>Log Out</span>
          </a>
        </div>

        <div className="guest-login-footer">
          <p>Velvet - Brand Voice Assessment</p>
        </div>
      </div>
    </div>
  );

  const renderUrlInput = () => (
    <div className="guest-url-input-screen">
      <div className="guest-header">
        <h1>Brand Voice Designer</h1>
        <p className="guest-subtitle">
          Design your brand's true voice by taking this guided journey.
        </p>
      </div>

      <div className="guest-form-card">
        <div className="guest-input-container">
          <label htmlFor="user-name" className="guest-label">
            YOUR NAME
          </label>
          <input
            id="user-name"
            type="text"
            className="guest-url-input"
            placeholder="Enter your name"
            value={userName}
            onChange={(e) => {
              setUserName(e.target.value);
              setError(null);
            }}
          />
        </div>

        <div className="guest-input-container">
          <label htmlFor="brand-name" className="guest-label">
            BRAND NAME
          </label>
          <input
            id="brand-name"
            type="text"
            className="guest-url-input"
            placeholder="Enter brand name"
            value={brandName}
            onChange={(e) => {
              setBrandName(e.target.value);
              setError(null);
            }}
          />
        </div>

        <div className="guest-input-container">
          <label htmlFor="url-input" className="guest-label">
            WEBSITE URL
          </label>
          <input
            id="url-input"
            type="text"
            className="guest-url-input"
            placeholder="yourbrand.com"
            value={urlInput}
            onChange={(e) => {
              setUrlInput(e.target.value);
              setError(null);
            }}
          />
        </div>

        {error && <div className="guest-error-message">{error}</div>}

        <button
          onClick={handleUrlSubmit}
          disabled={isExtractingUrls || !urlInput.trim() || !userName.trim() || !brandName.trim()}
          className="guest-primary-button"
        >
          {isExtractingUrls ? 'Analyzing Website...' : 'Continue'}
        </button>
      </div>
    </div>
  );

  const renderUrlSelection = () => (
    <div className="guest-url-input-screen">
      <div className="guest-header">
        <h1>Select a page to analyze</h1>
        <p className="guest-subtitle">Choose the page that best represents your brand</p>
      </div>

      <div className="guest-form-card">
        <div className="guest-url-selection-grid">
          {suggestedUrls.map((urlObj, index) => (
            <button
              key={index}
              onClick={() => handleUrlSelection(urlObj.url, urlObj.title)}
              className="guest-url-selection-button"
            >
              <span className="guest-url-title">{urlObj.title}</span>
              <span className="guest-url-text">{urlObj.url}</span>
            </button>
          ))}
        </div>

        <button onClick={() => setScreen('url-input')} className="guest-back-button" style={{ marginTop: '24px' }}>
          Back
        </button>
      </div>
    </div>
  );

  const renderLoading = () => (
    <div className="guest-loading-screen">
      <div className="guest-loading-spinner"></div>
      <h2>Analyzing your brand...</h2>
      <p>Creating personalized questions based on your website</p>
    </div>
  );

  const renderTournament = () => {
    const currentRound = tournamentState.round;

    // Get current pairing based on dynamic bracket logic
    const pairing = getTournamentPairing(currentRound, tournamentState.winners);

    if (!pairing) {
      return (
        <div className="guest-loading-screen">
          <div className="guest-loading-spinner"></div>
          <p>Tournament complete!</p>
        </div>
      );
    }

    // Get posts for the two voices in this matchup
    const postA = voicePosts[pairing.optionA];
    const postB = voicePosts[pairing.optionB];

    // If posts not available yet, show loading
    if (!postA || !postB) {
      console.warn(`Guest: Posts not available for round ${currentRound}: ${pairing.optionA} vs ${pairing.optionB}`);
      console.warn(`Guest: Available voices:`, Object.keys(voicePosts));

      return (
        <div className="guest-loading-screen">
          <div className="guest-loading-spinner"></div>
          <p>Loading question...</p>
        </div>
      );
    }

    const progressPercentage = (currentRound / 10) * 100;

    return (
      <div className="guest-tournament-screen">
        {/* Top header with title and progress */}
        <div className="guest-tournament-top-header">
          <div className="guest-tournament-title">
            <img src={VectorIcon} alt="" className="guest-tournament-title-icon" />
            BRAND VOICE DESIGNER
          </div>
          <div className="guest-tournament-step">{String(currentRound).padStart(2, '0')} / 10</div>
        </div>

        {/* Progress bar */}
        <div className="guest-progress-bar-container">
          <div className="guest-progress-bar-fill" style={{ width: `${progressPercentage}%` }}></div>
        </div>

        {/* Question */}
        <h2 className="guest-question-text">
          Which option feels more like {brandName}?
        </h2>

        {/* A/B comparison cards */}
        <div className="guest-comparison-cards">
          <div
            className={`guest-option-card ${!canProceed ? 'guest-option-disabled' : ''}`}
            onClick={() => handleAnswerSelect('A')}
          >
            <div className="guest-option-letter">A</div>
            <p className="guest-option-text">{postA}</p>
          </div>

          <span className="guest-or-divider">or</span>

          <div
            className={`guest-option-card ${!canProceed ? 'guest-option-disabled' : ''}`}
            onClick={() => handleAnswerSelect('B')}
          >
            <div className="guest-option-letter">B</div>
            <p className="guest-option-text">{postB}</p>
          </div>
        </div>

        {/* Context capsule */}
        <div className="guest-context-capsule">
          <svg className="guest-context-globe-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
          </svg>
          <span className="guest-context-url">{urlInput.replace(/^https?:\/\//, '')}</span>
          <div className="guest-context-separator"></div>
          <span className="guest-context-page">Context: {selectedUrlTitle || 'Selected Page'}</span>
        </div>

        {/* Timer indicator */}
        {!canProceed && (
          <div className="guest-timer-indicator">
            <div className="guest-timer-text">
              Read carefully... ({MIN_READ_TIME - timeElapsed}s remaining)
            </div>
            <div className="guest-timer-bar">
              <div
                className="guest-timer-bar-fill"
                style={{ width: `${(timeElapsed / MIN_READ_TIME) * 100}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderResults = () => {
    const persona = getPersonaInfo();

    if (!persona || Object.keys(tournamentState.allPersonas).length === 0) {
      return (
        <div className="guest-loading-screen">
          <div className="guest-loading-spinner"></div>
          <h2>Loading your results...</h2>
          <p>Preparing your brand voice profile</p>
        </div>
      );
    }

    return (
      <div className="guest-results-screen">
        {/* Top header with title and progress - matching tournament layout */}
        <div className="guest-tournament-top-header">
          <div className="guest-tournament-title">
            <img src={VectorIcon} alt="" className="guest-tournament-title-icon" />
            BRAND VOICE DESIGNER
          </div>
          <div className="guest-tournament-step">10 / 10</div>
        </div>

        {/* Progress bar - 100% complete */}
        <div className="guest-progress-bar-container">
          <div className="guest-progress-bar-fill" style={{ width: '100%' }}></div>
        </div>

        {/* Centered content container - 943px width */}
        <div className="guest-results-content-container">
          {/* Main title */}
          <h1 className="guest-results-main-title">Introducing the voice of your brand:</h1>

          {/* Explainer text */}
          <p className="guest-results-explainer">
            {persona.explainer}
          </p>

          {/* Two column layout for PERSONA and KEY TRAITS */}
          <div className="guest-results-columns">
            {/* Left column - PERSONA */}
            <div className="guest-results-column">
              <h3 className="guest-results-column-title">PERSONA</h3>
              <p className="guest-results-persona-text">
                {persona.introduction || persona.explainer}
              </p>
            </div>

            {/* Right column - KEY TRAITS */}
            <div className="guest-results-column">
              <h3 className="guest-results-column-title">KEY TRAITS:</h3>
              <div className="guest-results-traits-container">
                {persona.traits && persona.traits.slice(0, 5).map((trait, idx) => (
                  <div key={idx} className="guest-results-trait-row">
                    <span className="guest-results-trait-label">{trait.name}:</span>
                    <span className="guest-results-trait-value">{trait.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="guest-results-actions">
            <div className="guest-cta">
              <p>Want to unlock the full power of your brand voice?</p>
              <a href="/" className="guest-cta-button">
                Get Started with Velvet
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Timer useEffect - counts up and enables proceeding after MIN_READ_TIME
  useEffect(() => {
    let interval;
    if (isTimerActive && screen === 'tournament') {
      interval = setInterval(() => {
        setTimeElapsed(prev => {
          const newTime = prev + 1;
          if (newTime >= MIN_READ_TIME && !canProceed) {
            setCanProceed(true);
            setIsTimerActive(false);
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, screen, canProceed]);

  // Keyboard shortcuts
  useEffect(() => {
    if (screen !== 'tournament' || !canProceed) return;

    const handleKeyPress = (e) => {
      if (e.key === '1') handleAnswerSelect('A');
      if (e.key === '2') handleAnswerSelect('B');
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [screen, tournamentState.round, generatedQuestions, canProceed]);

  // Main render
  return (
    <div className="guest-brand-voice-designer">
      {screen === 'initializing' && renderLoading()}
      {screen === 'already-logged-in' && renderAlreadyLoggedIn()}
      {screen === 'guest-login' && renderGuestLogin()}
      {screen === 'url-input' && renderUrlInput()}
      {screen === 'url-selection' && renderUrlSelection()}
      {screen === 'loading' && renderLoading()}
      {screen === 'tournament' && renderTournament()}
      {screen === 'results' && renderResults()}
    </div>
  );
};

export default GuestBrandVoiceDesigner;
